// ./src/components/features/Venues/VenueCategorySelect/VenueCategorySelect.tsx
//
// Multi-select over the global venue-category vocabulary, grouped by place vs
// service. Reads options from the shared VenueCategoriesContext (loaded once).
// Reused by the venue drawer (value = categoryIds), the filter bar, and the bulk
// category modal. Modal-friendly (uses DRAWER_COMBOBOX_PROPS) so it works inside
// a drawer/sheet without fighting its focus trap.

import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useOptionalVenueCategoriesContext } from "@/contexts/VenueCategoriesContext";
import { DRAWER_COMBOBOX_PROPS } from "@/utils";
import { MultiSelectField } from "@/components/generic";

export interface VenueCategorySelectProps {
  value: string[];
  onChange: (ids: string[]) => void;
  label?: string;
  description?: string;
  placeholder?: string;
  error?: React.ReactNode;
  disabled?: boolean;
  size?: string;
  clearable?: boolean;
  /** Restrict the offered categories to these groups (e.g. ["service"] for vendors). Omit for all. */
  allowedGroups?: string[];
}

const GROUP_ORDER = ["place", "service"];

export const VenueCategorySelect: React.FC<VenueCategorySelectProps> = ({
  value,
  onChange,
  label,
  description,
  placeholder,
  error,
  disabled = false,
  size = "md",
  clearable = true,
  allowedGroups,
}) => {
  const { t } = useTranslation(["venues", "common"]);

  const ctx = useOptionalVenueCategoriesContext();
  // ⓘ Memoised: a fresh `[]` on every render would change what the memos below depend on (#129).
  const categories = useMemo(() => ctx?.categories ?? [], [ctx?.categories]);
  const loading = ctx?.loading ?? false;

  const data = useMemo(() => {
    const byGroup = new Map<string, { value: string; label: string }[]>();

    for (const category of categories) {
      if (!category.id) {
        continue;
      }

      const group = category.categoryGroup || "other";

      if (allowedGroups && !allowedGroups.includes(group)) {
        continue;
      }

      const items = byGroup.get(group) ?? [];
      items.push({ value: category.id, label: category.name || category.code || category.id });
      byGroup.set(group, items);
    }

    const groups = Array.from(byGroup.keys()).sort((a, b) => {
      const ai = GROUP_ORDER.indexOf(a);
      const bi = GROUP_ORDER.indexOf(b);

      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });

    return groups.map((group) => ({
      group: t(`venues:categoryGroups.${group}`, { defaultValue: group === "place" ? "Places" : "Services" }),
      items: (byGroup.get(group) ?? []).sort((a, b) => a.label.localeCompare(b.label)),
    }));
  }, [categories, t, allowedGroups]);

  return (
    <MultiSelectField
      label={label}
      description={description}
      placeholder={
        value.length === 0
          ? placeholder ?? t("venues:form.fields.categories.placeholder", { defaultValue: "Select categories" })
          : undefined
      }
      data={data}
      value={value}
      onChange={(_name, v) => onChange(v)}
      error={error}
      disabled={disabled || loading}
      searchable
      clearable={clearable}
      hidePickedOptions
      nothingFoundMessage={t("venues:form.fields.categories.empty", { defaultValue: "No categories found" })}
      comboboxProps={DRAWER_COMBOBOX_PROPS}
      size={size}
    />
  );
};
